import { API, Storage } from "src/domains/shared/utils/amplifyCompat";
import { DRONESQUARE_API_GATEWAY } from "src/domains/shared/constants/constants";
import { getGeoLogDeleteToken } from "../utils/getGeoLogDeleteToken";
import {
  CreateForm,
  DeleteFileType,
  ProjectType,
  Response,
  ResponseWithNothing,
  TIME_PROJECT,
  TIME_PROJECT_DETAIL,
} from "../../../../src/domains/projects-list/type";
import {
  unwrapEnvelopeList,
  unwrapEnvelopeRequired,
} from "src/domains/shared/utils/unwrapEnvelope";

import { GetMappingImages, UseGetProjectsProps } from "./projects.queries";
import {
  EtcDataType,
  LayerDataGroups,
  type OriginalDroneImageSize,
} from "./projects.models";
import { DxfMultiLayer } from "../../../../src/store/dxfGraphStore";
import { TileMatrix } from "src/domains/shared/api/gisLayer/gisLayer.model";
import { volumeFormat } from "../../../../src/domains/shared/utils/volumeFormat";

/** S3 목록 조회 한 페이지의 최대 객체 수 — 원본 크기 조회가 이미지 수가 아니라 이 단위로 나간다 */
const ORIGINAL_IMAGE_LIST_PAGE_SIZE = 1000;

export type LineType = "gis" | "line";

export type BimType = "fbx" | "las";

export type ProjectMeta = {
  /**
   * @description 원본 GSD
   */
  xy_z_gsd: number;

  /**
   * @description 이미지 기반 레스터의 GSD
   */
  xy_img_gsd: number;

  /**
   * @description xy 좌표계의 EPSG 코드
   */
  crs_epsg: number;

  /**
   * @description ["최소 줌 레벨", "최대 줌 레벨"]
   */
  tms_zoom_boundary: [number, number];

  /**
   * @description x_boundary ["X최소값", "X최대값"]
   */
  x_boundary: [number, number];

  /**
   * @description y_boundary ["Y최소값", "Y최대값"]
   */
  y_boundary: [number, number];

  /**
   * @description z_boundary ["최저 높이", "최고 높이"]
   */
  z_boundary: [number, number];

  /**
   * @description lng_boundary ["최저 경도", "최고 경도"]
   */
  lng_boundary: [number, number];

  /**
   * @description lat_boundary ["최저 위도", "최고 위도"]
   */
  lat_boundary: [number, number];

  /**
   * @description 다운샘플링 전 이미지 갯수
   */
  calibrated_image_count: number;

  /**
   * @description 다운샘플링 후 이미지 갯수
   */
  downsampled_image_count: number;

  camera_focal_length: number;

  /**
   * @description 카메라 센서 픽셀 크기(마이크로미터)
   */
  camera_sensor_pixel_size: number;

  /**
   * @description Px Py
   */
  camera_principal_point_offset: [number, number];
  mapping_date: string;
  capture_data: string;
  area: number;
  image_projection_area: number;
  matrix: TileMatrix;
};

class ProjectsRepository {
  /** 목록 조회 실패는 경계에서 오류로 승격한다 — 소비처는 `[]` 와 실패를 구별할 수 없다. */
  async getProjectsList({
    isOwn,
  }: UseGetProjectsProps): Promise<ProjectType[]> {
    const path = isOwn ? "projects?is_own=t" : "projects";
    const res = await API.get(DRONESQUARE_API_GATEWAY, path, {});
    return unwrapEnvelopeList<ProjectType>(res);
  }
  async getProjectById(id: number): Promise<Response<ProjectType>> {
    return API.get(DRONESQUARE_API_GATEWAY, `projects/${id}`, {});
  }

  async createProject(data: CreateForm) {
    return await API.post(DRONESQUARE_API_GATEWAY, "projects", { body: { ...data } });
  }

  async getTimeSeries(projectId: number): Promise<Response<TIME_PROJECT[]>> {
    return API.get(DRONESQUARE_API_GATEWAY, `projects/${projectId}/time_projects`, {});
  }

  async getTimeSeriesById(
    projectId: number,
    timeSeriesId: number
  ): Promise<Response<TIME_PROJECT_DETAIL>> {
    return API.get(
      DRONESQUARE_API_GATEWAY,
      `projects/${projectId}/time_projects/${timeSeriesId}`,
      {}
    );
  }

  /**
   * @param data timestampe => "2022-02-01"
   */
  async createTimeseries(data: {
    projectId: number;
    timestamp: string;
    comment: string;
  }) {
    return API.post(
      DRONESQUARE_API_GATEWAY,
      `projects/${data.projectId}/time_projects`,
      {
        body: { timestamp: data.timestamp, comment: data.comment },
      }
    );
  }

  async patchTimeseriesById(data: {
    timeSeriesId: number;
    projectId: number;
    timestamp: string;
    comment: string;
    title?: string;
  }) {
    return API.patch(
      DRONESQUARE_API_GATEWAY,
      `projects/${data.projectId}/time_projects/${data.timeSeriesId}`,
      {
        body: {
          timestamp: data.timestamp,
          comment: data.comment,
          title: data.title,
        },
      }
    );
  }

  async check3dDataExists(projectId: number) {
    const modelPath = `pipeline/3d_model/mesh/${projectId}/`;
    const { results } = await Storage.list(modelPath);
    if (results.length < 2) return false;
    return true;
  }

  async getProjectMappingDate(
    projectId: number | undefined,
    timeSeriesId: number | undefined
  ) {
    return API.get(
      DRONESQUARE_API_GATEWAY,
      `calculation/projects/${projectId}/time_projects/${timeSeriesId}/constant/metadata`,
      {}
    );
  }
  async patchProjectByID(data: any) {
    return API.patch(DRONESQUARE_API_GATEWAY, `projects/${data.id}`, {
      body: { ...data.data },
    });
  }
  async deleteProjectTimeseriesByID({
    projectId,
    timeSeriesId,
  }: {
    projectId: number | undefined;
    timeSeriesId: number | undefined;
  }) {
    if (!projectId || !timeSeriesId) return;
    return API.del(
      DRONESQUARE_API_GATEWAY,
      `projects/${projectId}/time_projects/${timeSeriesId}`,
      {}
    );
  }

  async getS3Imgs(
    id: number,
    type: "original" | "compressed" | "thumbnail",
    nextToken?: string
  ): Promise<any[]> {
    const response = await Storage.list(`${id}/drone_images/${type}/`, {
      pageSize: 50,
      nextToken,
    });
    if (!response.hasMore) return response.results;
    const nextResults = await this.getS3Imgs(id, type, response.nextToken);
    return [...response.results, ...nextResults];
  }

  async getS3ImgsWithMetadata(
    id: number,
    type: "original" | "compressed" | "thumbnail",
    nextToken?: string
  ): Promise<any[]> {
    const response = await Storage.list(`${id}/drone_images/${type}/`, {
      pageSize: 50,
      nextToken,
    });

    /**
     * 객체가 싣고 있는 **원본 파일명**(`x-amz-meta-name`) — 메타데이터가 없거나 조회에 실패하면 빈 문자열이다.
     *
     * S3 키의 파일명으로 대신 채우지 않는다 — 업로드 때 중복 회피로 재명명된 이름이라 원본과 다를 수 있고,
     * 그것을 폴더 배치 키로 쓰면 서버가 모르는 이름이 나간다. 화면에 그릴 이름의 대체는 목록 훅 한 곳이 한다.
     */
    const originFileNameOf = async (key: string | undefined): Promise<string> => {
      if (!key) return "";
      try {
        const properties = await Storage.getProperties(key);
        const name = properties?.metadata?.["name"];
        return name ? decodeURIComponent(name) : "";
      } catch {
        return "";
      }
    };

    const resultsWithMetadata = await Promise.all(
      response.results.map(async (item) => ({
        ...item,
        metadataName: await originFileNameOf(item.key),
      }))
    );

    if (!response.hasMore) return resultsWithMetadata;
    const nextResults = await this.getS3ImgsWithMetadata(
      id,
      type,
      response.nextToken
    );

    return [...resultsWithMetadata, ...nextResults];
  }

  /**
   * 회차의 드론 이미지 **업로드 원본** 크기 — 업로드가 올린 `pipeline/original_drone_images/{회차}/` 를 끝까지
   * 나열해 바로 아래 객체마다 파일명과 크기를 돌려준다(spec: project-drone-image-management).
   *
   * 크기는 목록 조회가 싣는 값을 그대로 쓴다 — 이미지마다 HEAD(`getProperties`)를 보내지 않으므로 요청 수는
   * 이미지 수가 아니라 페이지 수(⌈N/1000⌉)를 따른다. 썸네일 listing 의 객체 크기는 서버 파생물의 크기라 이 자리를
   * 대신할 수 없다. 행과의 짝짓기(원본 파일명의 NFC 비교)는 목록 훅이 한다 — 여기서는 키가 실은 이름을 그대로 낸다.
   */
  async getOriginalDroneImageSizes(
    timeSeriesId: number
  ): Promise<OriginalDroneImageSize[]> {
    const prefix = `pipeline/original_drone_images/${timeSeriesId}/`;
    const sizes: OriginalDroneImageSize[] = [];
    let nextToken: string | undefined;

    do {
      const page = await Storage.list(prefix, {
        pageSize: ORIGINAL_IMAGE_LIST_PAGE_SIZE,
        nextToken,
      });
      page.results.forEach((item: { key?: string; size?: number }) => {
        // 업로드는 prefix 바로 아래에만 올린다 — 폴더 표식(빈 이름)과 하위 경로 키는 원본 이미지가 아니다
        const name = item.key?.startsWith(prefix)
          ? item.key.slice(prefix.length)
          : "";
        if (!name || name.includes("/")) return;
        sizes.push({ name, size: item.size ?? null });
      });
      nextToken = page.hasMore ? page.nextToken : undefined;
    } while (nextToken);

    return sizes;
  }

  async getMappingImages({
    projectId,
    timeSeriesId,
    lng = 0,
    lat = 0,
    filter = "nearby",
  }: GetMappingImages) {
    return API.get(
      DRONESQUARE_API_GATEWAY,
      `calculation/projects/${projectId}/time_projects/${timeSeriesId}/function/nearby-images?filter=${filter}&point_z=false&lng=${lng}&lat=${lat}`,
      {}
    );
  }

  async postS3ImgsProcess(
    id: number | undefined,
    type: "original" | "compressed" | "thumbnail"
  ) {
    const list = await Storage.list(`${id}/drone_images/${type}/`, {
      pageSize: undefined, // "ALL" is handled by default in compat layer
    });
    // 이미지 키 추출 로직
    const onlyKeysFromList = list.results.map((item) => {
      if (!item.key) return 0;
      const imgKey = item.key.split("/").pop()?.split(".").shift();
      return Number(imgKey);
    });

    const process = await API.post(
      DRONESQUARE_API_GATEWAY,
      `calculation/${id}/constant/image-status`,
      { body: { images: onlyKeysFromList } }
    );

    return process;
  }

  async getFavoriteDroneImageList(
    projectId: number,
    timeSeriesId: number
  ): Promise<string[]> {
    const res = await API.get(
      DRONESQUARE_API_GATEWAY,
      `projects/${projectId}/time_projects/${timeSeriesId}/favourites?type=images`,
      {}
    );
    return unwrapEnvelopeList<string>(res);
  }

  async patchFavoriteDroneImageList(arg: {
    projectId: number;
    timeSeriesId: number;
    imageName: string;
  }) {
    return API.post(
      DRONESQUARE_API_GATEWAY,
      `projects/${arg.projectId}/time_projects/${arg.timeSeriesId}/favourites?image-name=${arg.imageName}`,
      {}
    );
  }

  async getS3EtcData(id: number, type: EtcDataType) {
    if (type === "gis") {
      const layerList = await Storage.list(`pipeline/user_layer/${id}/gis`);
      return layerList.results;
    }
    if (type === "line") {
      const layerList = await Storage.list(`pipeline/user_layer/${id}/line`);
      return layerList.results;
    }
    const specificList = await Storage.list(`client/${id}/${type}`);
    return specificList.results.filter(
      (item) =>
        !item.key?.includes("/thumbnails/") && !item.key?.includes("/previews/")
    );
  }

  async getS3GcpData(id: number) {
    const gcpList = await Storage.list(`pipeline/user_gcp/${id}/`);
    return gcpList.results;
  }

  /** 4 카테고리(gis/linear/bim/las) 레이어 데이터 통합 조회 — LayerData 탭의 단일 표시 소스 */
  async getLayerData(projectId: number): Promise<LayerDataGroups> {
    const res = await API.get(
      DRONESQUARE_API_GATEWAY,
      `projects/${projectId}/layer-data`,
      {}
    );
    // 응답 봉투 { status, data } 를 경계에서 언랩 — 실패는 오류로 승격된다.
    // 이전의 `(res as any).data ?? res` 는 실패 봉투를 그대로 통과시켜
    // 소비처가 `data[group]` 을 undefined 로 읽고 빈 목록으로 접게 만들었다.
    return unwrapEnvelopeRequired<LayerDataGroups>(res, "layer-data");
  }

  async getS3MultiLayer(id: number, fileName: string, type: DxfMultiLayer) {
    const layerList = await Storage.list(
      `pipeline/multi_layer/${id}/${fileName}/${type}`
    );

    return layerList.results;
  }

  // 3D 에서 보이는 경로는 구축경로
  // https://dronesquare.notion.site/S3-3a2b6a48feb24c4c9dcf960ef879cac1?pvs=4 경로정리 링크
  builtBimStorage = {
    fbx: async (id: number) => await Storage.list(`pipeline/bim/${id}/`),
    las: async (id: number) => await Storage.list(`pipeline/las/${id}/`),
    lineDxfLas: async (id: number) => await Storage.list(`${id}/multi_layer`), // 레이어 > 선형레이어 > 계획고,지층 구축 경로
  };

  async getBuiltS3BimData(id: number, dataType: BimType) {
    return await this.builtBimStorage[dataType](id);

    // ifc 변환 구현되면 교체할 주소
    // return Storage.list(`pipeline/ifc/${id}`);
  }

  async getS3Cesium3DTileJsonUrl(
    id: number,
    nextToken: string | undefined = undefined
  ): Promise<string | undefined> {
    const {
      results,
      hasMore,
      nextToken: newToken,
    } = await Storage.list(`${id}/mesh_3d_tiles`, {
      nextToken,
    });

    const tileJsonUrlResults = results
      .filter((file) => !file.key?.includes("Data/"))
      .find((file) => file.key?.endsWith(".json"));

    if (!tileJsonUrlResults) {
      if (!hasMore) return undefined;
      return this.getS3Cesium3DTileJsonUrl(id, newToken);
    }

    return tileJsonUrlResults.key;
  }

  async deleteEtcData(files: DeleteFileType[]) {
    const promises = Array.from(files).map((item: any) => {
      const key: string = item.key;
      if (/\/(image|video)\//.test(key)) {
        const projectId = key.split("/")[1];
        const token = getGeoLogDeleteToken(key);
        return API.del(
          DRONESQUARE_API_GATEWAY,
          `projects/${projectId}/geologs/${token}`,
          {}
        );
      }
      return Storage.remove(`${key}`);
    });
    return Promise.all(promises);
  }

  async deleteS3Data(files: string[]) {
    const promises = files.map((file) => {
      return Storage.remove(file);
    });
    return Promise.all(promises);
  }

  /**
   * S3 파일의 메타데이터에서 원본 파일 경로를 추출
   */
  private async getOriginalImagePath(
    thumbnailKey: string
  ): Promise<string | null> {
    try {
      const projectId = thumbnailKey.split("/").shift();
      if (!projectId) return null;

      // const { Metadata } = (await Storage.get(thumbnailKey, {
      //   download: true,
      // })) as unknown as { Metadata?: { name?: string } };
      const result = await Storage.getProperties(thumbnailKey);

      const Metadata = result.metadata;

      const imageOriginName = Metadata?.name ? decodeURI(Metadata.name) : null;

      if (!imageOriginName) return null;

      return `pipeline/original_drone_images/${projectId}/${imageOriginName}`;
    } catch {
      return null;
    }
  }

  /**
   * 특정 projectId의 모든 원본 이미지 삭제
   **/
  private async listAllOriginalImages(
    projectId: number,
    nextToken?: string
  ): Promise<string[]> {
    const prefix = `pipeline/original_drone_images/${projectId}/`;
    const response = await Storage.list(prefix, {
      pageSize: 1000,
      nextToken,
    });

    const keys = response.results
      .map((item) => item.key)
      .filter(Boolean) as string[];

    if (!response.hasMore) return keys;

    const nextKeys = await this.listAllOriginalImages(
      projectId,
      response.nextToken
    );
    return [...keys, ...nextKeys];
  }

  /**
   * 특정 프리미스 하위의 모든 키 리스트 조회
   */
  private async listAllKeysUnderPrefix(
    prefix: string,
    nextToken?: string
  ): Promise<string[]> {
    const response = await Storage.list(prefix, {
      pageSize: 1000,
      nextToken,
    });

    const keys = response.results
      .map((item) => item.key)
      .filter(Boolean) as string[];

    if (!response.hasMore) return keys;

    const nextKeys = await this.listAllKeysUnderPrefix(
      prefix,
      response.nextToken
    );
    return [...keys, ...nextKeys];
  }

  /**
   * 프로젝트의 모든 원본 이미지 삭제
   * pipeline/original_drone_images/${projectId}/ prefix로 시작하는 모든 객체 삭제
   */
  async deleteAllOriginalImages(projectId: number): Promise<void> {
    // 1. 모든 관련 키 수집 (원본, 썸네일, 압축 이미지)
    const [originalKeys, thumbnailKeys, compressedKeys] = await Promise.all([
      this.listAllOriginalImages(projectId),
      this.listAllKeysUnderPrefix(`${projectId}/drone_images/thumbnail/`),
      this.listAllKeysUnderPrefix(`${projectId}/drone_images/compressed/`),
    ]);

    const allKeys = [...originalKeys, ...thumbnailKeys, ...compressedKeys];

    if (allKeys.length === 0) return;

    const MAX_CONCURRENT_DELETES = 10;
    const MAX_RETRIES = 3;

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));

    const isRetryableError = (error: unknown) => {
      const message =
        typeof error === "object" && error && "message" in error
          ? String((error as any).message)
          : "";

      // 브라우저 리소스 부족/네트워크 계열은 재시도 가치가 큼
      return (
        message.includes("ERR_INSUFFICIENT_RESOURCES") ||
        message.includes("ERR_NETWORK") ||
        message.includes("Network Error") ||
        message.includes("NetworkError")
      );
    };

    const removeWithRetry = async (key: string) => {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          await Storage.remove(key);
          return;
        } catch (error) {
          const isLastAttempt = attempt === MAX_RETRIES;
          if (!isLastAttempt && isRetryableError(error)) {
            // 지수 백오프 + 약간의 지터로 동시 재시도 폭주 방지
            const backoffMs =
              300 * 2 ** attempt + Math.floor(Math.random() * 200);
            await sleep(backoffMs);
            continue;
          }
          throw error;
        }
      }
    };

    // 동시 삭제 제한 (Promise.all로 한 번에 수천개 실행 방지)
    const failed: { key: string; error: unknown }[] = [];
    const executing = new Set<Promise<void>>();

    for (const key of allKeys) {
      const p = removeWithRetry(key).catch((error) => {
        failed.push({ key, error });
      });
      executing.add(p);
      p.finally(() => executing.delete(p));

      if (executing.size >= MAX_CONCURRENT_DELETES) {
        await Promise.race(executing);
      }
    }

    await Promise.all(executing);

    if (failed.length > 0) {
      // 호출자가 상위에서 에러를 잡아 사용자에게 재시도/안내할 수 있도록 throw
      const sample = failed
        .slice(0, 5)
        .map((f) => f.key)
        .join(", ");
      throw new Error(
        `원본 이미지 삭제 실패: ${failed.length}/${allKeys.length} (예: ${sample})`
      );
    }
  }

  /**
   * 썸네일/압축 이미지는 유지하고, 메타데이터에서 추출한 원본 파일만 삭제
   */
  async deleteS3Imgs(files: (string | { key: string })[]): Promise<void> {
    const MAX_CONCURRENT_DELETES = 10;
    const failed: { key: string; error: unknown }[] = [];
    const executing = new Set<Promise<void>>();

    for (const item of files) {
      const key = typeof item === "string" ? item : item.key;

      const p = (async () => {
        try {
          // 1. 원본 이미지 경로 찾기 및 삭제
          const originalPath = await this.getOriginalImagePath(key);
          if (originalPath) {
            await Storage.remove(originalPath);
          }

          // 2. 썸네일 자체 삭제
          await Storage.remove(key);

          // 3. 압축 이미지 삭제 (있는 경우)
          if (key.includes("/drone_images/thumbnail/")) {
            const compressedKey = key.replace("/thumbnail/", "/compressed/");
            await Storage.remove(compressedKey);
          }
        } catch (error) {
          failed.push({ key, error });
        }
      })();

      executing.add(p);
      p.finally(() => executing.delete(p));

      if (executing.size >= MAX_CONCURRENT_DELETES) {
        await Promise.race(executing);
      }
    }

    await Promise.all(executing);

    if (failed.length > 0) {
      console.error("일부 이미지 삭제 실패:", failed);
    }
  }

  async getSearchProjectList(text: string): Promise<ProjectType[]> {
    // enabled: !!text 로 게이팅되는 경로 — 빈 검색어에서는 조회 자체가 나가지 않는다.
    if (!text) return new Promise<ProjectType[]>(() => null);
    // text 는 사용자가 친 검색어다. API.get 의 경로 문자열은 그대로 URL 로 파싱되므로 쿼리 값으로
    // 인코딩해야 '&'·'#'·'+'·'%' 가 든 검색어도 잘리거나 바뀌지 않고 서버에 원문 그대로 간다.
    const res = await API.get(
      DRONESQUARE_API_GATEWAY,
      `projects?term=${encodeURIComponent(text)}`,
      {}
    );
    return unwrapEnvelopeList<ProjectType>(res);
  }
  async getIssueCategory(
    id: number
  ): Promise<{ status: "Success" | "Failed"; categories: string[] }> {
    return API.get(DRONESQUARE_API_GATEWAY, `projects/${id}/category`, {});
  }
  async createIssueCategory(arg: { id: number; keyword: string }) {
    return API.post(DRONESQUARE_API_GATEWAY, `projects/${arg.id}/category`, {
      body: { keyword: arg.keyword },
    });
  }
  async patchIssueCategory(arg: { id: number; before: string; after: string }) {
    return API.patch(DRONESQUARE_API_GATEWAY, `projects/${arg.id}/category`, {
      body: { before: arg.before, after: arg.after },
    });
  }
  async deleteIssueCategory(arg: { id: number; keyword: string }) {
    // keyword 는 사용자가 지은 유형 이름이다. API.del 의 경로 문자열은 그대로 URL 로 파싱되므로 쿼리 값으로
    // 인코딩해야 '&'·'#'·'+'·'%' 가 든 이름도 잘리거나 바뀌지 않고 서버에 원문 그대로 간다.
    return API.del(
      DRONESQUARE_API_GATEWAY,
      `projects/${arg.id}/category?keyword=${encodeURIComponent(arg.keyword)}`,
      {}
    );
  }
  /**
   * 매핑 요청. 자동 구축일 때만 `{"auto": true}` 를 싣고, 그 밖에는 이 기능 이전과 똑같이 본문 없이
   * 보낸다 — 백엔드는 본문 부재를 수동 경로로 읽는다.
   */
  async postMappingRequest(
    id: number,
    { auto }: { auto: boolean }
  ): Promise<ResponseWithNothing> {
    return API.post(
      DRONESQUARE_API_GATEWAY,
      `mapping_request/${id}/user_request`,
      auto ? { body: { auto: true } } : {}
    );
  }

  /**
   * GCP 자동 인식 토글 저장(소유자 전용). 409(불가·GCP 파일 없음·매핑 진행 중)·403·404 는
   * 게이트웨이가 non-2xx 로 이미 reject 하고, 성공 응답의 봉투는 여기서 승격한다.
   */
  async putGcpAutoDetect(
    timeSeriesId: number,
    enabled: boolean
  ): Promise<{ gcp_auto_detect: boolean }> {
    const res = await API.put(
      DRONESQUARE_API_GATEWAY,
      `mapping_request/${timeSeriesId}/gcp_auto_detect`,
      { body: { enabled } }
    );
    return unwrapEnvelopeRequired<{ gcp_auto_detect: boolean }>(
      res,
      "gcp_auto_detect"
    );
  }

  async getProjectMeta({
    projectId,
    timeSeriesId,
  }: {
    projectId: number;
    timeSeriesId: number;
  }) {
    return (await API.get(
      DRONESQUARE_API_GATEWAY,
      `calculation/projects/${projectId}/time_projects/${timeSeriesId}/constant/metadata`,
      {}
    )) as Promise<ProjectMeta>;
  }

  async getProjectOrthomosaicImage(timeSeriesId: number) {
    const img = await Storage.get(`${timeSeriesId}/manifold/orthomosaic.png`);
    const data = await fetch(img);
    if (data.status === 200) {
      return img;
    } else {
      return "";
    }
  }

  async getTimeSeriesFilesSizes(timeSeriesId: number) {
    const orthomosaic = await Storage.list(
      `${timeSeriesId}/manifold/orthomosaic.tif`
    );
    const pointCloud = await Storage.list(
      `${timeSeriesId}/manifold/point_cloud.las`
    );
    const mesh = await Storage.list(`${timeSeriesId}/manifold/mesh.fbx`);
    const colorizedDem = await Storage.list(
      `${timeSeriesId}/manifold/colorized_dem.tif`
    );

    const orthomosaicSize = orthomosaic?.results[0]?.size ?? 0;
    const pointCloudSize = pointCloud?.results[0]?.size ?? 0;
    const meshSize = mesh?.results[0]?.size ?? 0;
    const colorizedDemSize = colorizedDem?.results[0]?.size ?? 0;

    return {
      orthomosaicSize: volumeFormat(orthomosaicSize, 2),
      pointCloudSize: volumeFormat(pointCloudSize, 2),
      meshSize: volumeFormat(meshSize, 2),
      colorizedDemSize: volumeFormat(colorizedDemSize, 2),
      totalSize: volumeFormat(
        orthomosaicSize + pointCloudSize + meshSize + colorizedDemSize,
        2
      ),
    };
  }

  async getDroneImagePreview(s3Key: string | null) {
    if (!s3Key) return null;

    const [projectId, , , imgName] = s3Key.split("/");
    const thumbnailUrl = `${projectId}/drone_images/thumbnail/${imgName}`;

    try {
      return await Storage.get(thumbnailUrl);
    } catch {
      return await Storage.get(s3Key);
    }
  }
}
export default new ProjectsRepository();
